import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { KeyRound, Loader2 } from "lucide-react";
import adminApi from "./adminApi";

export default function ChangePassword() {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [userId, setUserId] = useState("");
  const [email, setEmail] = useState("");

  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [toast, setToast] = useState(null);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 2600);
  };

  useEffect(() => {
    const fetchMe = async () => {
      setLoading(true);
      setLoadError("");
      try {
        const res = await adminApi.get("/admin/auth/me");
        setUserId(res.data.userId || "");
        setEmail(res.data.email || "");
      } catch (err) {
        setLoadError(err.response?.data?.message || "Could not load your profile.");
      } finally {
        setLoading(false);
      }
    };
    fetchMe();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");

    if (!oldPassword || !newPassword || !confirmPassword) {
      setFormError("All fields are required.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setFormError("New Password and Confirm New Password do not match.");
      return;
    }

    setSaving(true);
    try {
      await adminApi.put("/admin/auth/change-password", {
        oldPassword,
        newPassword,
        confirmPassword,
      });
      showToast("success", "Password changed successfully.");
      setOldPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      setFormError(err.response?.data?.message || "Something went wrong. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="h-full flex flex-col">
      <div className="shrink-0 mb-3">
        <h3 className="font-display font-semibold text-lg text-[#0B2545]">Change Password</h3>
        <p className="text-xs text-slate-400 mt-0.5">Update the password for your admin account.</p>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 max-w-lg">
        {loading ? (
          <div className="flex items-center justify-center py-10 text-slate-400 text-sm gap-2">
            <Loader2 size={18} className="animate-spin" /> Loading...
          </div>
        ) : loadError ? (
          <p className="text-sm text-red-500 py-6 text-center">{loadError}</p>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
            {/* Hidden dummy fields absorb the browser's autofill before the real fields */}
            <input type="text" name="fake-username" autoComplete="username" className="hidden" />
            <input type="password" name="fake-password" autoComplete="new-password" className="hidden" />

            <div>
              <label className="text-xs font-medium text-slate-500 mb-1.5 block">
                User ID <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={userId}
                readOnly
                className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm bg-slate-50 text-slate-500 cursor-not-allowed"
              />
            </div>

            <div>
              <label className="text-xs font-medium text-slate-500 mb-1.5 block">
                Email ID <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={email}
                readOnly
                className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm bg-slate-50 text-slate-500 cursor-not-allowed"
              />
            </div>

            <div>
              <label className="text-xs font-medium text-slate-500 mb-1.5 block">
                Old Password <span className="text-red-500">*</span>
              </label>
              <input
                type="password"
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
                maxLength={20}
                autoFocus
                autoComplete="new-password"
                className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0EA5A4]/30 focus:border-[#0EA5A4] transition"
              />
            </div>

            <div>
              <label className="text-xs font-medium text-slate-500 mb-1.5 block">
                New Password <span className="text-red-500">*</span>
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                maxLength={20}
                autoComplete="new-password"
                className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0EA5A4]/30 focus:border-[#0EA5A4] transition"
              />
            </div>

            <div>
              <label className="text-xs font-medium text-slate-500 mb-1.5 block">
                Confirm New Password <span className="text-red-500">*</span>
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                maxLength={20}
                autoComplete="new-password"
                className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0EA5A4]/30 focus:border-[#0EA5A4] transition"
              />
            </div>

            {formError && (
              <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                {formError}
              </p>
            )}

            <button
              type="submit"
              disabled={saving}
              className="flex items-center justify-center gap-2 bg-[#0B2545] hover:bg-[#143A63] text-white font-medium rounded-lg py-2.5 px-5 transition disabled:opacity-60"
            >
              {saving ? <Loader2 size={16} className="animate-spin" /> : <KeyRound size={15} />}
              {saving ? "Saving..." : "Submit"}
            </button>
          </form>
        )}
      </div>

      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className={`fixed bottom-6 right-6 z-50 px-4 py-3 rounded-lg shadow-lg text-sm font-medium ${
              toast.type === "success" ? "bg-[#0B2545] text-white" : "bg-red-600 text-white"
            }`}
          >
            {toast.message}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
